import { SectionBanner } from '@otter-ai/den-components';
import { useEffect } from 'react';
import { useExperimentInGroup } from '@/experiment/experiments-client';
import { useUsageNotification } from '@/shared/hooks/subscription/useUsageNotification';
import { useModalKey } from '@/shared/modals';
import { UploadArea } from './Components/UploadArea';
import { UploadDashboard } from './Components/UploadDashboard';
import { UploadQuotaInfo } from './Components/UploadQuotaInfo';
import { useCancelAll, useUploadPreSend } from './hooks/helperHooks';
import { useUploadItems } from './hooks/useUploadItems';
import type {
  ImportQuotas,
  UploadItem,
  UploadModalContextData,
} from './types/types';

export function Upload({
  quota,
  setImportQuota,
  updateUploadItems,
  setAbortFunction,
  contextData,
}: {
  quota: ImportQuotas;
  setImportQuota: (quota: ImportQuotas) => void;
  updateUploadItems: (items: UploadItem[]) => void;
  setAbortFunction: (abortFn: (items: UploadItem[]) => void) => void;
  contextData: UploadModalContextData;
}) {
  const isAdhocBannersEnabled = useExperimentInGroup(
    'adhoc_recording_banners',
    'enabled',
  );
  const isExternalASRModalEnabled = useExperimentInGroup(
    'external_asr_modal',
    'treatment',
  );
  const { hideModal } = useModalKey('upload-file-modal');
  const { openSalesAssistedLimitReachedOverlay } = useUsageNotification();

  const { list, isQuotaExceeded } = useUploadItems(
    quota,
    setImportQuota,
    contextData,
  );
  const cancelAll = useCancelAll();
  useUploadPreSend(setImportQuota);

  // Update the parent component whenever the upload list changes
  useEffect(() => {
    updateUploadItems(list);
  }, [list, updateUploadItems]);

  // Provide the abort function to the modal
  useEffect(() => {
    setAbortFunction(cancelAll);
  }, [cancelAll, setAbortFunction]);

  useEffect(() => {
    if (isExternalASRModalEnabled && quota.externalASRError) {
      hideModal();
      openSalesAssistedLimitReachedOverlay({ overrideRemainingMinutes: 0 });
    }
  }, [
    quota.externalASRError,
    isExternalASRModalEnabled,
    hideModal,
    openSalesAssistedLimitReachedOverlay,
  ]);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="h-0 md:h-2" />
      {isAdhocBannersEnabled && list.length > 0 && (
        <div className="px-4 pb-3">
          <SectionBanner color="brand" data-testid="upload-guidance-banner">
            <SectionBanner.Content description="Keep the laptop and browser running during the upload." />
          </SectionBanner>
        </div>
      )}
      <UploadDashboard list={list} />
      <UploadArea list={list} isQuotaExceeded={isQuotaExceeded} quota={quota} />
      <UploadQuotaInfo quota={quota} />
    </div>
  );
}
